import { useRouter } from "next/router";
import React from "react";
import { cs } from "../../core/classSet";
import { getIndustrySlug, getSearchQuery, getTopicSlug } from "../../core/getParam";
import { useDidUpdate } from "../../core/hooks/useDidUpdate";
import logger from "../../core/logger";
import { getHomePageRoute } from "../../core/routes";
import { IIndustry, ITopic } from "../../typings/filterEntities";
import Button from "../Button/Button";
import { SearchIcon } from "../Icons/Icons";
import SearchInput from "../SearchInput/SearchInput";
import Select, { IOption } from "../Select/Select";
import styles from "./FilterBar.module.css";

interface IProps {
  filterBarRef?: React.RefObject<HTMLDivElement>;
  industries: IIndustry[];
  location?: "homeHeader" | "homeHero" | "pageHero";
  topics: ITopic[];
}

export default function FilterBar({
  filterBarRef,
  industries,
  location = "homeHero",
  topics,
}: IProps) {
  const router = useRouter();
  const industrySlug = getIndustrySlug(router);
  const topicSlug = getTopicSlug(router);
  const routerQuery = getSearchQuery(router) ?? "";

  const [industry, setIndustry] = React.useState(industrySlug);
  const [topic, setTopic] = React.useState(topicSlug);
  const [searchQuery, setSearchQuery] = React.useState(routerQuery);

  useDidUpdate(() => {
    if (industrySlug !== industry) {
      setIndustry(industrySlug);
    }
  }, [industrySlug]);

  useDidUpdate(() => {
    if (topicSlug !== topic) {
      setTopic(topicSlug);
    }
  }, [topicSlug]);

  useDidUpdate(() => {
    if (routerQuery !== searchQuery) {
      setSearchQuery(routerQuery);
    }
  }, [routerQuery]);

  const handleChangeIndustry = React.useCallback((v: IOption | null) => {
    if (v) {
      setIndustry(v.value);
    }
  }, []);

  const handleChangeTopic = React.useCallback((v: IOption | null) => {
    if (v) {
      setTopic(v.value);
    }
  }, []);

  const industryOptions = React.useMemo(
    () => [
      { value: "all", label: "All industries" },
      ...industries.map((i) => ({ value: i.slug, label: i.label })),
    ],
    [industries],
  );

  const topicOptions = React.useMemo(
    () => [
      { value: "all", label: "All types" },
      ...topics.map((i) => ({ value: i.slug, label: i.label })),
    ],
    [topics],
  );

  const handleSubmit: React.FormEventHandler = (ev) => {
    ev.preventDefault();
    router
      .push(
        getHomePageRoute({
          industry,
          topic,
          searchQuery,
        }),
      )
      .catch((reason) => logger.error(reason));
  };

  return (
    <form
      className={location === "homeHeader" ? styles.homeHeaderWrapper : ""}
      onSubmit={handleSubmit}
    >
      <div
        className={cs(
          styles.root,
          location === "homeHeader" && styles.rootHomeHeader,
          location === "pageHero" && styles.rootPageHero,
        )}
        ref={filterBarRef}
      >
        <div
          className={cs(
            location === "pageHero" && styles.inner,
            location === "homeHeader" && styles.headerInner,
          )}
        >
          <ul className={styles.grid}>
            <li className={styles.block}>
              <Select
                className={styles.select}
                defaultValue={industryOptions[0]}
                inHeader={location === "homeHeader"}
                label="Industry"
                options={industryOptions}
                value={industry}
                onChange={handleChangeIndustry}
              />
            </li>
            <li className={styles.block}>
              <Select
                className={styles.select}
                defaultValue={topicOptions[0]}
                inHeader={location === "homeHeader"}
                label="Type"
                options={topicOptions}
                value={topic}
                onChange={handleChangeTopic}
              />
            </li>
            <li className={styles.searchBlock}>
              <h4 className={styles.heading}>Search</h4>
              <SearchInput
                inputClassName={styles.search}
                searchQuery={searchQuery}
                onChange={(ev) => setSearchQuery(ev.target.value)}
              />
            </li>
          </ul>
        </div>
        <div className={styles.button}>
          <Button iconLeft={<SearchIcon />} type="submit">
            Search
          </Button>
        </div>
      </div>
    </form>
  );
}
